import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  setCamps,
  setCampsLoading,
  setCampsError,
  setSelectedCamp,
} from '../../../redux/slices/ykVisitSlice';
import ykVisitService from '../services/ykVisitService';
import './YkVisitSelection.css';

const formatDate = (dateStr) => {
  if (!dateStr) return 'TBD';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

const YkVisitSelection = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const centerId = searchParams.get('centerId');
  const country = searchParams.get('country') || 'USA';

  const { camps, campsLoading, campsError, selectedCamp } =
    useSelector((state) => state.ykVisit);

  useEffect(() => {
    if (centerId) {
      fetchCamps();
    }
  }, [centerId]);

  const fetchCamps = async () => {
    try {
      dispatch(setCampsLoading(true));
      const data = await ykVisitService.getCampsByCenter(centerId, country);
      dispatch(setCamps(data || []));
    } catch (err) {
      dispatch(setCampsError('Failed to load visit camps. Please try again later.'));
      console.error(err);
    } finally {
      dispatch(setCampsLoading(false));
    }
  };

  const handleSelectCamp = (camp) => {
    dispatch(setSelectedCamp(camp));
  };

  const handleProceed = () => {
    if (selectedCamp) {
      navigate(`/yk-visit/registration?campId=${selectedCamp.camp_Id}&centerId=${centerId}`);
    }
  };

  return (
    <div className="ykselect-page">
      <div className="container-custom">
        <div className="ykselect-header">
          <button onClick={() => navigate('/yk-visit/center')} className="ykselect-back-btn">
            ← Back to Centers
          </button>
          <h1 className="ykselect-title">Select Visit Camp</h1>
          <p className="ykselect-subtitle">Choose a camp to register for a YK Visit</p>
        </div>

        {campsLoading && (
          <div className="ykvisit-loading">
            <div className="inline-block">
              <div className="ykvisit-spinner"></div>
            </div>
            <p className="ykvisit-loading-text">Loading visit camps...</p>
          </div>
        )}

        {campsError && (
          <div className="ykvisit-error-banner">
            <p className="ykvisit-error-text">{campsError}</p>
            <button onClick={fetchCamps} className="ykvisit-error-retry">
              Try again
            </button>
          </div>
        )}

        {!campsLoading && camps.length > 0 && (
          <div className="ykselect-layout">
            <div className="ykselect-camp-list">
              {camps.map((camp) => (
                <div
                  key={camp.camp_Id}
                  className={`ykselect-camp-card ${
                    selectedCamp?.camp_Id === camp.camp_Id ? 'selected' : ''
                  }`}
                  onClick={() => handleSelectCamp(camp)}
                >
                  {selectedCamp?.camp_Id === camp.camp_Id && (
                    <div className="ykselect-tick-badge">
                      <svg className="ykselect-tick-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}

                  <div className="ykselect-camp-body">
                    <div className="ykselect-camp-top">
                      <h3 className="ykselect-camp-name">{camp.camp_Name}</h3>
                      {camp.camp_Fee > 0 ? (
                        <span className="ykselect-fee-badge">${camp.camp_Fee}</span>
                      ) : (
                        <span className="ykselect-free-badge">Free</span>
                      )}
                    </div>

                    {camp.camp_Description && (
                      <div
                        className="ykselect-camp-desc"
                        dangerouslySetInnerHTML={{ __html: camp.camp_Description }}
                      />
                    )}

                    <div className="ykselect-dates-grid">
                      <div className="ykselect-date-cell">
                        <p className="ykselect-date-label">Start Date</p>
                        <p className="ykselect-date-value">{formatDate(camp.camp_Date_From)}</p>
                      </div>
                      <div className="ykselect-date-cell">
                        <p className="ykselect-date-label">End Date</p>
                        <p className="ykselect-date-value">{formatDate(camp.camp_Date_To)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="ykselect-sidebar">
              {selectedCamp ? (
                <div className="ykselect-summary-card">
                  <h3 className="ykselect-summary-title">Visit Summary</h3>
                  <div className="ykselect-summary-fields">
                    <div>
                      <p className="ykselect-summary-field-label">Camp Name</p>
                      <p className="ykselect-summary-field-value">{selectedCamp.camp_Name}</p>
                    </div>
                    <div>
                      <p className="ykselect-summary-field-label">Duration</p>
                      <p className="ykselect-summary-field-value">
                        {formatDate(selectedCamp.camp_Date_From)} –{' '}
                        {formatDate(selectedCamp.camp_Date_To)}
                      </p>
                    </div>
                    <div className="ykselect-summary-divider">
                      <p className="ykselect-summary-field-label">Camp Fee</p>
                      <p className="ykselect-summary-fee">
                        {selectedCamp.camp_Fee > 0 ? `$${selectedCamp.camp_Fee}` : 'Free'}
                      </p>
                    </div>
                  </div>
                  <button onClick={handleProceed} className="ykselect-proceed-btn">
                    Proceed to Registration →
                  </button>
                </div>
              ) : (
                <div className="ykselect-empty-card">
                  <div className="ykselect-empty-icon">🏕️</div>
                  <p className="ykselect-empty-hint">Select a camp to view details and proceed</p>
                </div>
              )}
            </div>
          </div>
        )}

        {!campsLoading && camps.length === 0 && !campsError && (
          <div className="ykselect-no-camps">
            <div className="ykselect-no-camps-icon">🏕️</div>
            <p className="ykselect-no-camps-text">No visit camps available for this center.</p>
            <button onClick={() => navigate('/yk-visit/center')} className="ykselect-no-camps-link">
              ← Choose a different center
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default YkVisitSelection;
